import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Site, SiteConfig } from '@99designs/site-navigation/lib/Site';
import { trackEvent } from '@99designs/tracking';
import { colors } from '../../../shared/lib/styles';
import alternativeSites from '../../lib/alternativeSites';
import Contact from '../Contact';
import Grid from '../Grid';
import Head from '../Head';
import PageSection from '../PageSection';
import ComparisonTableSection from './ComparisonTableSection';
import FAQSection from './FAQSection';
import HeroSection from './HeroSection';
import HowItWorksSection from './HowItWorksSection';
import RecommendationSection from './RecommendationSection';

const pageName = 'web-builder-landing';

interface Props {
    currentPath: string;
    site: Site;
}

// Copied from CategoryPageLayout
const SectionDivider = styled(Grid).attrs({
    as: 'hr',
})`
    width: 100%;
    height: auto;
    display: block;
    box-sizing: border-box;
    background: transparent;
    margin: 0 auto;

    &:before {
        content: '';
        display: block;
        width: 100%;
        height: 2px;
        background: ${colors.grey300};
    }
`;

function WebsiteProjectPage({ currentPath, site }: Props) {
    const supportPhoneNumber = (site.siteConfig as SiteConfig)['phone.support'];
    const languageCode = site.getLanguageCode();

    useEffect(() => {
        trackEvent('Viewed Product Page', { category: 'web-builder' });
    }, []);

    return (
        <>
            <Head
                // TODO - SEO
                title={'Website builder'}
                description={''}
                meta={
                    <>
                        {alternativeSites.map(([altLocale, domain]) => (
                            <link
                                rel="alternate"
                                hrefLang={altLocale}
                                key={altLocale}
                                href={`https://${domain}${currentPath}`}
                            />
                        ))}
                    </>
                }
                site={site}
            />

            <PageSection
                id="hero"
                pageName={pageName}
                paddingLeft={['halfGutter', 'gutter']}
                paddingRight={['halfGutter', 'gutter']}
            >
                <HeroSection languageCode={languageCode} />
            </PageSection>

            <PageSection
                id="how-it-works"
                pageName={pageName}
                paddingTop={['space10', 'space10', 'space14', 'space14']}
                paddingBottom={['space7', 'space7', 'space7', 'space7']}
            >
                <HowItWorksSection languageCode={languageCode} />
            </PageSection>

            <PageSection
                id="faq"
                pageName={pageName}
                paddingBottom={['space7', 'space7', 'space7', 'space7']}
            >
                <Grid>
                    <FAQSection languageCode={languageCode} />
                </Grid>
            </PageSection>

            <PageSection
                id="recommendations"
                pageName={pageName}
                paddingTop={['space10', 'space10', 'space14', 'space14']}
                paddingBottom={['space7', 'space7', 'space7', 'space7']}
            >
                <Grid>
                    <RecommendationSection languageCode={languageCode} />
                </Grid>
            </PageSection>

            <SectionDivider />

            <PageSection
                id="compare"
                pageName={pageName}
                paddingTop={['space10', 'space10', 'space14', 'space14']}
                paddingBottom={['space7', 'space7', 'space7', 'space7']}
            >
                <ComparisonTableSection />
            </PageSection>

            <PageSection
                id="contact"
                pageName={pageName}
                paddingTop={['space10', 'space10', 'space14', 'space14']}
                paddingBottom={['space7', 'space7', 'space7', 'space7']}
            >
                <Grid>
                    <Contact
                        heading={__("We're here to help")}
                        description={__(
                            'Questions? Our kind, happy and humble customer support team would love to hear from you.'
                        )}
                        supportPhoneNumber={supportPhoneNumber}
                    />
                </Grid>
            </PageSection>
        </>
    );
}

export default WebsiteProjectPage;
